import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import './i18n/config'; // Initialize i18n
import { useTranslation } from 'react-i18next';
import { toast, Toaster } from "sonner";
import { FaHome, FaBook, FaBoxOpen, FaShoppingCart, FaHeart, FaSearch, FaCog, FaMagic, FaPlus, FaSignOutAlt, FaSeedling, FaLink, FaAd, FaLightbulb, FaImage, FaCheckCircle, FaTrash, FaGlobe, FaRobot, FaLanguage, FaShieldAlt, FaEnvelope, FaLock } from "react-icons/fa";
import "@/App.css";
import { getSessionId } from "./utils/session";
import { AuthProvider, useAuth } from "./context/AuthContext";
import UpgradeModal from "./components/UpgradeModal";
import HomePage from "./pages/HomePage";
import RecipesPage from "./pages/RecipesPage";
import RecipeDetailPage from "./pages/RecipeDetailPage";
import PantryPage from "./pages/PantryPage";
import MatchFinderPage from "./pages/MatchFinderPage";
import ShoppingListPage from "./pages/ShoppingListPage";
import FavoritesPage from "./pages/FavoritesPage";
import SettingsPage from "./pages/SettingsPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import BillingCancelledPage from "./pages/BillingCancelledPage";
import AddRecipePage from "./pages/AddRecipePage";
import EditRecipePage from "./pages/EditRecipePage";
import AdminPage from "./pages/AdminPage";
import AdminSyncPage from "./pages/AdminSyncPage";
import AdminSandboxPage from "./pages/AdminSandboxPage";
import AdminIngredientsPage from "./pages/AdminIngredientsPage";
import AdminMatchImagesPage from "./pages/AdminMatchImagesPage";
import AdminFixApprovals from "./pages/AdminFixApprovals";
import AdminDeleteRecipes from "./pages/AdminDeleteRecipes";
import AdminSeedIngredients from "./pages/AdminSeedIngredients";
import AdminBadgesPage from "./pages/AdminBadgesPage";
import SetupPage from "./pages/SetupPage";
import AdminCommentsPage from "./pages/AdminCommentsPage";
import TipsPage from "./pages/TipsPage";
import CreateTipPage from "./pages/CreateTipPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import NotificationBell from "./components/NotificationBell";
import { useNewRecipesCount } from "./hooks/useNewRecipes";
import WelcomeOnboarding from "./components/WelcomeOnboarding";
import {
  isGuestOnboardingCompleted,
  isFirstOnboardingSession,
  markFirstOnboardingSession,
  GUEST_ONBOARDING_EVENT,
} from "./utils/guestOnboarding";
import Analytics from "./components/Analytics";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage";
import MembersPage from "./pages/MembersPage";
import BrixInfoPage from "./pages/BrixInfoPage";
import SlushBookAI from './pages/SlushBookAI';
import AdminLinksPage from "./pages/AdminLinksPage";
import AdminAdsPage from "./pages/AdminAdsPage";
import AdminSponsoredRowsPage from "./pages/AdminSponsoredRowsPage";
import AdminTranslationsPage from "./pages/AdminTranslationsPage";
import AdminBackupsPage from "./pages/AdminBackupsPage";
import AdminEmptySearchesPage from "./pages/AdminEmptySearchesPage";
import AdminBrixOutliersPage from "./pages/AdminBrixOutliersPage";
import AdminIngredientsMissingBrixPage from "./pages/AdminIngredientsMissingBrixPage";
import AdminResyncIngredientBrixPage from "./pages/AdminResyncIngredientBrixPage";
import AdminNormalizeTargetBrixPage from "./pages/AdminNormalizeTargetBrixPage";
import AdminClassifyRecipesPage from "./pages/AdminClassifyRecipesPage";
import AdminBrixQualityPage from "./pages/AdminBrixQualityPage";
import NotFoundPage from "./pages/NotFoundPage";
import AdminEmailPage from "./pages/AdminEmailPage";
import BillingRedirectPage from "./pages/BillingRedirectPage";
import BillingResumeCheckoutPage from "./pages/BillingResumeCheckoutPage";
import AdvertisePage from "./pages/AdvertisePage";
import AdminAdRequestsPage from "./pages/AdminAdRequestsPage";
import AdminRecipeTranslationsPage from "./pages/AdminRecipeTranslationsPage";
import AdminImportRecipes from "./pages/AdminImportRecipes";
import SharedRecipePage from "./pages/SharedRecipePage";
import ManageSharesPage from "./pages/ManageSharesPage";
import AdSlot from "./components/AdSlot";
import { hasPremiumAccess, getUserSubscription, SUBSCRIPTIONS } from "./config/subscriptions";
import GuidePage from "./pages/GuidePage";
import GuideDetailPage from "./pages/GuideDetailPage";
import TipRedirect from "./components/TipRedirect";
import FloatingAIButton from "./components/FloatingAIButton";

// Backend URL configuration - uses environment variable
// Deployment system will automatically set REACT_APP_BACKEND_URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
export const API = `${BACKEND_URL}/api`;
export const BACKEND = BACKEND_URL;

// Configure axios to send cookies with all requests (required for SameSite=none)
axios.defaults.withCredentials = true;

// Add axios interceptor to include session token from localStorage in all requests
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Send anonym guest-id på ALLE requests. Backend bruger den kun til
    // gæste-baseret dedup i reactions-systemet — ingen tracking.
    try {
      const gid = localStorage.getItem('sb_guest_id');
      if (gid) {
        config.headers['X-Guest-Id'] = gid;
      }
    } catch (_e) {
      /* ignore */
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

console.log('[App] Hostname:', window.location.hostname);
console.log('[App] Backend URL:', BACKEND_URL);

// Navigation Component
const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isGuest } = useAuth();
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [lockedReason, setLockedReason] = React.useState(null);  // shows UpgradeModal when set
  const { t } = useTranslation();
  const desktopMenuRef = React.useRef(null);
  const mobileMenuRef = React.useRef(null);

  // Resolve guest state once per render. `isGuest` from useAuth is a function.
  // "Guest" = kun anonym besøgende (ikke logget ind). Registrerede Free/Light
  // brugere har adgang til grundfunktioner og skal ikke se lås-ikoner i navigation.
  const guest = typeof isGuest === 'function' ? isGuest() : !user;

  // Track whether an anonymous guest has already finished onboarding so we
  // can hide the manual Guide button in the topbar. Re-reads localStorage
  // whenever the onboarding component fires the change event.
  const [guestOnboardingDone, setGuestOnboardingDone] = React.useState(
    () => isGuestOnboardingCompleted()
  );
  // Session-scoped marker: when set, we are in the user's FIRST session
  // since they (re-)started onboarding. The Guide button stays visible the
  // whole time so the user can step back through the guide; only the next
  // browser session / login flips it off.
  const [firstSession, setFirstSession] = React.useState(
    () => isFirstOnboardingSession()
  );
  // For anonymous visitors, declare the current tab/window as their first
  // onboarding session as soon as they land — but only if they haven't
  // already completed onboarding in some past session. This way refreshing
  // the page does not lose the "still in first session" state, but closing
  // the tab does.
  React.useEffect(() => {
    if (!user && !isGuestOnboardingCompleted() && !isFirstOnboardingSession()) {
      markFirstOnboardingSession();
    }
  }, [user]);
  React.useEffect(() => {
    const sync = () => {
      setGuestOnboardingDone(isGuestOnboardingCompleted());
      setFirstSession(isFirstOnboardingSession());
    };
    window.addEventListener(GUEST_ONBOARDING_EVENT, sync);
    return () => window.removeEventListener(GUEST_ONBOARDING_EVENT, sync);
  }, []);
  // Guests should only see the manual Guide button on their *first* visit.
  // "Guest" here means EITHER an anonymous visitor (no user) OR a signed-up
  // free-tier user (role === 'guest'). For anonymous visitors we read a
  // localStorage flag; for signed-up guest-role users we trust the
  // server-side `onboarding_completed` boolean on their user record.
  // Authenticated users with elevated roles (pro / editor / admin) ALWAYS
  // keep access to the Guide button regardless of either flag.
  //
  // Refinement (user request): even after onboarding is "completed" we
  // keep the Guide button visible for the rest of the SAME session, so a
  // user who just finished can still go back and replay or step through
  // the guide. It only hides starting from the next session/login.
  const guestHasCompletedOnboarding = !user
    ? guestOnboardingDone
    : (user?.role === 'guest' || user?.role === 'user') && user?.onboarding_completed === true;
  const guestShouldHideGuide = guestHasCompletedOnboarding && !firstSession;
  const showGuestGuideButton = guest && !guestShouldHideGuide;

  // Guide is a first-visit helper — Pro-abonnenter og employees har ingen
  // brug for det. Skjul topbar-knap + user-menu-entry for dem.
  // Free/Light-tier ser stadig knappen (de vil evt. lære platformen at kende).
  const showGuideButton = !guestShouldHideGuide && !hasPremiumAccess(user);

  // "Nye opskrifter siden sidste besøg" — drives the badge on /recipes nav.
  const { newCount: newRecipesCount } = useNewRecipesCount();

  // Close dropdown when clicking outside (check both refs)
  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (isUserMenuOpen) {
        const clickedInsideDesktop = desktopMenuRef.current && desktopMenuRef.current.contains(e.target);
        const clickedInsideMobile = mobileMenuRef.current && mobileMenuRef.current.contains(e.target);
        
        if (!clickedInsideDesktop && !clickedInsideMobile) {
          setIsUserMenuOpen(false);
        }
      }
    };
    // Changed from 'mousedown' to 'click' to allow Link navigation to happen first
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen]);

  const navItems = [
    { path: "/", icon: FaHome, label: t('nav.home') },
    { path: "/recipes", icon: FaBook, label: t('nav.recipes') },
    { path: "/match", icon: FaMagic, label: t('nav.match'), locked: guest, lockReason: 'general' },
    { path: "/shopping", icon: FaShoppingCart, label: t('nav.shoppingList'), locked: guest, lockReason: 'general' },
    { path: "/favorites", icon: FaHeart, label: t('nav.favorites'), locked: guest, lockReason: 'general' },
    { path: "/settings", icon: FaCog, label: t('nav.settings') },
  ];

  /**
   * Shared menu-item list used by BOTH the desktop dropdown and the mobile
   * dropdown. Previously these were two hard-coded copies which drifted out
   * of sync — the mobile dropdown was missing the entire admin section.
   * Keep this as the single source of truth so both surfaces always match.
   */
  const renderUserMenuItems = (closeMenu, { compact = false } = {}) => {
    if (!user) return null;
    const itemBase = `flex items-center gap-3 px-4 ${compact ? 'py-2' : 'py-3'} text-sm hover:bg-gray-50`;
    const linkCls = `${itemBase} text-gray-700 cursor-pointer active:bg-gray-100`;
    const sectionHeaderCls = `px-4 py-2 mt-2 text-xs font-semibold text-gray-500 tracking-wider border-t border-gray-100`;
    return (
      <>
        {/* Section: Bruger Platformen */}
        <Link to="/profile" onClick={closeMenu} className={linkCls} data-testid="usermenu-profile">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Min profil
        </Link>
        {showGuideButton && (
          <button
            type="button"
            onClick={() => {
              closeMenu();
              window.dispatchEvent(new Event('slushbook:open-onboarding'));
            }}
            className={`${linkCls} w-full text-left`}
            data-testid="usermenu-open-onboarding"
          >
            <FaLightbulb className="w-4 h-4 text-amber-500" />
            Velkomst-guide
          </button>
        )}
        <Link to="/guides" onClick={closeMenu} className={linkCls} data-testid="usermenu-tips">
          <FaLightbulb className="w-4 h-4 text-yellow-500" />
          Tips & Guides
        </Link>
        <Link to="/favorites" onClick={closeMenu} className={linkCls} data-testid="usermenu-favorites">
          <FaHeart className="w-4 h-4" />
          Favoritter
        </Link>

        {/* Section: Moderation — editor sees just the Sandbox.
            Admin sees the full Admin block below which already includes it. */}
        {user.role === 'editor' && (
          <>
            <div className={sectionHeaderCls}>Moderation</div>
            <Link to="/admin/sandbox" onClick={closeMenu} className={linkCls} data-testid="usermenu-sandbox-editor">
              <FaSearch className="w-4 h-4" />
              Sandkasse
            </Link>
          </>
        )}

        {/* Section: Admin (only when user.role === 'admin').
            Reorganised Feb 2026 into topical sub-sections so the dropdown
            doesn't scroll off the viewport. Each sub-group is opened by a
            sectionHeaderCls label. Order matches user's proposed hierarchy:
            Opskrifter · Brix & Kvalitet · System · Indhold. */}
        {user.role === 'admin' && (
          <>
            <div className={sectionHeaderCls}>Admin · Overblik</div>
            <Link to="/members" onClick={closeMenu} className={linkCls} data-testid="usermenu-members">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Medlemmer
            </Link>
            <Link to="/admin/sandbox" onClick={closeMenu} className={linkCls} data-testid="usermenu-sandbox">
              <FaSearch className="w-4 h-4" />
              Sandkasse
            </Link>
            <Link to="/admin" onClick={closeMenu} className={linkCls} data-testid="usermenu-admin-panel">
              <FaCog className="w-4 h-4" />
              Dashboard
            </Link>

            <div className={sectionHeaderCls}>Opskrifter</div>
            <Link to="/admin/ingredients" onClick={closeMenu} className={linkCls} data-testid="usermenu-ingredients">
              <FaSeedling className="w-4 h-4" />
              Ingredienser
            </Link>
            <Link to="/admin/match-images" onClick={closeMenu} className={linkCls} data-testid="usermenu-match-images">
              <FaImage className="w-4 h-4" />
              Match Billeder
            </Link>
            <Link to="/admin/seed-ingredients" onClick={closeMenu} className={linkCls} data-testid="usermenu-seed-ingredients">
              <FaSeedling className="w-4 h-4" />
              Seed Ingredienser
            </Link>
            <Link to="/admin/fix-approvals" onClick={closeMenu} className={linkCls} data-testid="usermenu-fix-approvals">
              <FaCheckCircle className="w-4 h-4" />
              Godkend Alle
            </Link>
            <Link to="/admin/recipe-translations" onClick={closeMenu} className={linkCls} data-testid="usermenu-recipe-translations">
              <FaBook className="w-4 h-4" />
              Opskrift Oversættelser
            </Link>
            <Link to="/admin/delete-recipes" onClick={closeMenu} className={`${itemBase} text-red-600 hover:bg-red-50`} data-testid="usermenu-delete-recipes">
              <FaTrash className="w-4 h-4" />
              Slet Alle Opskrifter
            </Link>

            <div className={sectionHeaderCls}>Brix &amp; Kvalitet</div>
            <Link to="/admin/brix-quality" onClick={closeMenu} className={linkCls} data-testid="admin-menu-brix-quality-hub-link">
              <FaCog className="w-4 h-4" />
              Åbn Brix &amp; Kvalitet-hub →
            </Link>

            <div className={sectionHeaderCls}>System</div>
            <Link to="/settings" onClick={closeMenu} className={linkCls} data-testid="admin-menu-settings-link">
              <FaCog className="w-4 h-4" />
              {t('nav.settings')}
            </Link>
            <Link to="/admin/backups" onClick={closeMenu} className={linkCls} data-testid="admin-menu-backups-link">
              <FaShieldAlt className="w-4 h-4" />
              Backup &amp; Restore
            </Link>
            <Link to="/admin/email" onClick={closeMenu} className={linkCls} data-testid="admin-menu-email-link">
              <FaEnvelope className="w-4 h-4" />
              Email &amp; Mailflow
            </Link>
            <Link to="/admin/sync-translations" onClick={closeMenu} className={`${itemBase} text-blue-600 hover:bg-blue-50`} data-testid="usermenu-sync-translations">
              <FaLanguage className="w-4 h-4" />
              Sync Oversættelser
            </Link>

            <div className={sectionHeaderCls}>Indhold</div>
            <Link to="/admin/ads" onClick={closeMenu} className={linkCls} data-testid="usermenu-ads">
              <FaAd className="w-4 h-4" />
              Reklamer
            </Link>
            <Link to="/admin/sponsored-rows" onClick={closeMenu} className={linkCls} data-testid="usermenu-sponsored-rows">
              <FaAd className="w-4 h-4" />
              Sponsor-rækker
            </Link>
            <Link to="/admin/links" onClick={closeMenu} className={linkCls} data-testid="usermenu-links">
              <FaLink className="w-4 h-4" />
              Leverandør Links
            </Link>
            <Link to="/admin/empty-searches" onClick={closeMenu} className={linkCls} data-testid="admin-menu-empty-searches-link">
              <FaSearch className="w-4 h-4" />
              Søgninger uden resultat
            </Link>
          </>
        )}

        {/* Section: Settings + Logout — Settings shown here only for non-admins;
            for admins it's already listed under the "System" section above. */}
        {user.role !== 'admin' && (
          <Link to="/settings" onClick={closeMenu} className={`${linkCls} border-t border-gray-100 mt-2`} data-testid="usermenu-settings">
            <FaCog className="w-4 h-4" />
            {t('nav.settings')}
          </Link>
        )}
        <button
          onClick={async () => {
            closeMenu();
            try {
              await logout();
            } catch (err) {
              console.error('[App] Logout error:', err);
            }
            // Hard navigation — unmounts ALL components and refetches the
            // login page from scratch. A previous client-side navigate('/login')
            // left admin pages mounted; their useEffects refetched with a
            // missing token, threw, and produced a blank screen until refresh.
            window.location.href = '/login';
          }}
          className={`flex items-center gap-3 w-full px-4 ${compact ? 'py-2' : 'py-3'} text-sm text-red-600 hover:bg-red-50 border-t border-gray-100 cursor-pointer active:bg-red-100`}
          data-testid="usermenu-logout"
        >
          <FaSignOutAlt className="w-4 h-4" />
          {t('nav.logout')}
        </button>
      </>
    );
  };

  return (
    <nav className="border-b border-gray-200 sticky top-0 z-40 shadow-sm relative" style={{backgroundColor: '#115DA8'}}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* SlushBook smiley icon — visible on mobile, hidden on desktop (the SLUSHBOOK text logo is shown center-page so we use the smiley here to avoid duplication) */}
          <Link to="/" className="flex-shrink-0 lg:hidden" data-testid="nav-logo">
            <img
              src="/slushbook-smiley.png"
              alt="SlushBook"
              className="h-10 w-10 rounded-lg"
            />
          </Link>
          
          {/* Desktop Navigation - Right side only */}
          <div className="hidden lg:flex items-center gap-4 ml-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isLocked = !!item.locked;
              if (isLocked) {
                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => setLockedReason(item.lockReason || 'general')}
                    data-testid={`nav-${item.label.toLowerCase()}-locked`}
                    title={t('nav.premiumRequired', 'Kræver medlemskab')}
                    aria-label={`${item.label} — ${t('nav.premiumRequired', 'Kræver medlemskab')}`}
                    className="nav-link flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-white/55 hover:text-white hover:bg-white/10 cursor-pointer"
                  >
                    <Icon />
                    <span className="text-sm font-medium">{item.label}</span>
                    <FaLock className="text-[10px] opacity-80" />
                  </button>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`nav-link flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    isActive ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <Icon />
                  <span className="text-sm font-medium">{item.label}</span>
                  {item.path === '/recipes' && newRecipesCount > 0 && (
                    <span
                      data-testid="nav-recipes-new-badge"
                      title={`${newRecipesCount} ${newRecipesCount === 1 ? 'ny opskrift' : 'nye opskrifter'} siden sidste besøg`}
                      className="px-2 py-0.5 bg-yellow-400 text-gray-900 text-[10px] font-bold rounded-full"
                    >
                      {newRecipesCount > 9 ? '9+' : newRecipesCount} {newRecipesCount === 1 ? 'ny' : 'nye'}
                    </span>
                  )}
                  {item.badge && (
                    <span className="px-2 py-0.5 bg-yellow-400 text-gray-900 text-[10px] font-bold rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
            
            {/* Notifications & User Info / Login */}
            {user ? (
              <div ref={desktopMenuRef} className="relative pl-6 border-l border-white/20 flex items-center gap-4">
                {/* Help / re-launch onboarding — visible to every signed-in
                    user EXCEPT free-tier guest-role users who have already
                    finished onboarding *in a previous session*. We hide it
                    for them so they can't manually replay the wizard and
                    bypass guest restrictions. */}
                {showGuideButton && (
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('slushbook:open-onboarding'))}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors text-sm"
                    title="Genstart velkomst-guiden"
                    data-testid="open-onboarding-btn"
                    aria-label="Genstart velkomst-guiden"
                  >
                    <FaLightbulb className="w-4 h-4" />
                    <span className="hidden lg:inline">Guide</span>
                  </button>
                )}

                {/* Notification Bell */}
                <NotificationBell />
                
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-tour="settings-menu"
                >
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-blue-600 font-semibold">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <div className="text-left">
                    <div className="text-sm font-medium text-white">{user?.name || 'User'}</div>
                    <div className="text-xs text-white/70">{(getUserSubscription(user) || SUBSCRIPTIONS.free).name}</div>
                  </div>
                  <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* Dropdown Menu — max-height + scroll so it never falls off the viewport on smaller screens */}
                {isUserMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 overflow-y-auto overscroll-contain"
                    style={{ maxHeight: 'calc(100vh - 80px)' }}
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-800">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                    {renderUserMenuItems(() => setIsUserMenuOpen(false), { compact: true })}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {showGuestGuideButton && (
                  <button
                    type="button"
                    onClick={() => window.dispatchEvent(new Event('slushbook:open-onboarding'))}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white/90 hover:text-white hover:bg-white/10 transition-colors text-sm"
                    title="Genstart velkomst-guiden"
                    data-testid="open-onboarding-btn-guest"
                    aria-label="Genstart velkomst-guiden"
                  >
                    <FaLightbulb className="w-4 h-4" />
                    <span className="hidden lg:inline">Guide</span>
                  </button>
                )}
                <Link
                  to="/login?mode=login"
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-white/90 font-medium"
                  data-testid="desktop-login-button"
                >
                  Log ind
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div ref={mobileMenuRef} className="absolute right-4 top-4 lg:hidden">
        {user ? (
          <div className="relative">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('[App] Mobile menu toggled');
                setIsUserMenuOpen(!isUserMenuOpen);
              }}
              className="flex items-center justify-center p-3 bg-white/10 backdrop-blur-md rounded-full hover:bg-white/20 transition-all cursor-pointer active:bg-white/30"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              data-testid="mobile-profile-button"
              data-tour="settings-menu"
            >
              <FaCog size={24} className="text-white" />
            </button>
            
            {/* Dropdown menu — shares its item list with the desktop dropdown
                via renderUserMenuItems() so admin links can never be missing
                from one surface again. */}
            {isUserMenuOpen && (
              <div
                className="fixed right-4 top-20 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 overflow-y-auto overscroll-contain"
                style={{
                  display: 'block',
                  visibility: 'visible',
                  // Leave room for both the top nav (80px) AND the fixed
                  // bottom navigation (~72px). On iOS we also add the safe-
                  // area inset so the last menu item is never hidden behind
                  // the home indicator or the bottom bar.
                  maxHeight: 'calc(100vh - 80px - 72px - env(safe-area-inset-bottom, 0px))',
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                }}
                onClick={(e) => { e.stopPropagation(); }}
                data-testid="mobile-user-menu"
              >
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-800">{user.name}</div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>
                {renderUserMenuItems(() => setIsUserMenuOpen(false))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {showGuestGuideButton && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('slushbook:open-onboarding'))}
                className="p-2 rounded-lg text-white hover:bg-white/10"
                title="Guide"
                data-testid="mobile-open-onboarding-btn-guest"
                aria-label="Genstart velkomst-guiden"
              >
                <FaLightbulb className="w-4 h-4" />
              </button>
            )}
            <Link
              to="/login?mode=login"
              className="px-3 py-2 bg-white text-blue-600 rounded-lg hover:bg-white/90 font-medium text-sm"
              data-testid="mobile-login-button"
            >
              Log ind
            </Link>
            <Link
              to="/signup"
              className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium text-sm"
              data-testid="mobile-signup-button"
            >
            Tilmeld
            </Link>
          </div>
        )}
      </div>
      {/* Upgrade modal triggered when a guest clicks a locked nav item */}
      <UpgradeModal
        isOpen={!!lockedReason}
        onClose={() => setLockedReason(null)}
        reason={lockedReason || 'general'}
        currentUser={user}
        requiredTier="auth"
      />
    </nav>
  );
};

const App = () => {
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const id = getSessionId();
    setSessionId(id);
  }, []);

  if (!sessionId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Analytics />
          {/* Global mount so the Guide-button + onboarding event listener
              are alive on EVERY route, for both guests and logged-in users.
              Single instance — see HomePage useEffect for the auto-open-on-
              first-visit trigger that dispatches the same event. */}
          <WelcomeOnboarding open={false} onClose={() => {}} />
          <AppContent sessionId={sessionId} />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

// Separate component to use AuthContext
const AppContent = ({ sessionId }) => {
  const { user, loading, login } = useAuth();
  const location = useLocation();
  
  // Use user.id as sessionId for logged-in users, otherwise use guest sessionId
  // More robust check: user must be object with id property
  const effectiveSessionId = (user && typeof user === 'object' && user.id) ? user.id : sessionId;
  
  // Debug logging
  React.useEffect(() => {
    console.log('[App] User object:', user);
    console.log('[App] User type:', typeof user);
    console.log('[App] User.id:', user?.id);
    console.log('[App] User:', user ? `${user.email} (id: ${user.id})` : 'Guest');
    console.log('[App] Guest sessionId:', sessionId);
    console.log('[App] Effective sessionId:', effectiveSessionId);
  }, [user, sessionId, effectiveSessionId]);

  const { t } = useTranslation();
  // Mobile-nav lock-icons: kun anonyme gæster skal se hængelåse. Registrerede
  // Free/Light-brugere har adgang til Match/Indkøbsliste/Favoritter.
  const guest = !user;
  const [mobileLockedReason, setMobileLockedReason] = React.useState(null);
  // "Nye opskrifter siden sidste besøg" — badge on the mobile bottom nav.
  const { newCount: newRecipesCount } = useNewRecipesCount();

  const navItems = [
    { path: "/", icon: FaHome, label: t('nav.home') },
    { path: "/recipes", icon: FaBook, label: t('nav.recipes') },
    { path: "/match", icon: FaMagic, label: t('nav.match') },
    ...(user ? [{ path: "/shopping", icon: FaShoppingCart, label: t('nav.shoppingList') }] : []),
    ...(user ? [{ path: "/favorites", icon: FaHeart, label: t('nav.favorites') }] : []),
    { path: "/settings", icon: FaCog, label: t('nav.settings') },
  ];

  // Don't show nav on auth pages. For unauthenticated visitors, `/` also
  // renders the landing/login hero — hide nav there so guests get the same
  // clean landing regardless of whether they arrived at `/` or `/login`.
  const authPaths = ['/login', '/signup', '/forgot-password', '/reset-password'];
  const isAuthPage = authPaths.includes(location.pathname) || (location.pathname === '/' && !user);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Background decorations */}
      <div className="bg-decoration bg-decoration-1"></div>
      <div className="bg-decoration bg-decoration-2"></div>

      <div className="relative z-10">
        {!isAuthPage && <Navigation />}

        <main className={`container mx-auto px-4 py-6 ${!isAuthPage ? 'mb-20 lg:mb-6' : ''}`}>
          <Routes>
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage onLogin={login} />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/profile" element={<ProfilePage />} />

            {/* App Routes */}
            {/* Root path: for unauthenticated visitors the landing page
                (LoginPage's hero + "Se opskrifterne"-CTA + collapsible login)
                is the correct start-side — HomePage is only shown to signed-in
                users. This makes `/` and `/login` render identical hero for
                guests, so bookmarks + direct root URL match the flow. */}
            <Route path="/" element={user ? <HomePage sessionId={effectiveSessionId} /> : <LoginPage onLogin={login} />} />
            <Route path="/recipes" element={<RecipesPage sessionId={effectiveSessionId} />} />
            <Route path="/recipes/:id" element={<RecipeDetailPage sessionId={effectiveSessionId} />} />
            <Route path="/advertise" element={<AdvertisePage />} />
            <Route path="/pantry" element={<PantryPage sessionId={effectiveSessionId} />} />
            <Route path="/match" element={<MatchFinderPage sessionId={effectiveSessionId} />} />
            <Route path="/shopping" element={<ShoppingListPage sessionId={effectiveSessionId} />} />
            <Route path="/favorites" element={<FavoritesPage sessionId={effectiveSessionId} />} />
            <Route path="/settings" element={<SettingsPage sessionId={effectiveSessionId} />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancelled" element={<BillingCancelledPage />} />
            <Route path="/billing/resume-checkout" element={<BillingResumeCheckoutPage />} />
            <Route path="/guide" element={<GuidePage />} />
            <Route path="/add-recipe" element={<AddRecipePage sessionId={effectiveSessionId} />} />
            <Route path="/edit-recipe/:id" element={<EditRecipePage sessionId={effectiveSessionId} />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/brix-info" element={<BrixInfoPage />} />
            <Route path="/ai" element={<SlushBookAI />} />
            <Route path="/admin" element={<AdminPage sessionId={effectiveSessionId} />} />
            <Route path="/admin/sync-translations" element={<AdminSyncPage />} />
            <Route path="/admin/sandbox" element={<AdminSandboxPage sessionId={effectiveSessionId} />} />
            <Route path="/admin/comments" element={<AdminCommentsPage />} />
            <Route path="/tips" element={<TipsPage />} />
            <Route path="/tips/create" element={<CreateTipPage />} />
            {/* Del 1 — Tips & Guides: nye ruter + bagudkompatible redirects. */}
            <Route path="/guides" element={<TipsPage />} />
            <Route path="/guides/:slug" element={<GuideDetailPage />} />
            <Route path="/tips/:slug/edit" element={<CreateTipPage />} />
            <Route path="/tips/:id" element={<TipRedirect />} />
            <Route path="/setup" element={<SetupPage />} />
            <Route path="/admin/ingredients" element={<AdminIngredientsPage sessionId={effectiveSessionId} />} />
            <Route path="/admin/seed-ingredients" element={<AdminSeedIngredients />} />
            <Route path="/admin/match-images" element={<AdminMatchImagesPage />} />
            <Route path="/admin/fix-approvals" element={<AdminFixApprovals />} />
            <Route path="/admin/delete-recipes" element={<AdminDeleteRecipes />} />
            <Route path="/admin/links" element={<AdminLinksPage sessionId={effectiveSessionId} />} />
            <Route path="/admin/ads" element={<AdminAdsPage />} />
            <Route path="/admin/sponsored-rows" element={<AdminSponsoredRowsPage />} />
            <Route path="/admin/ad-requests" element={<AdminAdRequestsPage />} />
            <Route path="/admin/badges" element={<AdminBadgesPage />} />
            <Route path="/admin/translations" element={<AdminTranslationsPage />} />
            <Route path="/admin/backups" element={<AdminBackupsPage />} />
            <Route path="/admin/email" element={<AdminEmailPage />} />
            <Route path="/admin/empty-searches" element={<AdminEmptySearchesPage />} />
            <Route path="/admin/brix-outliers" element={<AdminBrixOutliersPage />} />
            <Route path="/admin/ingredients-missing-brix" element={<AdminIngredientsMissingBrixPage />} />
            <Route path="/admin/resync-ingredient-brix" element={<AdminResyncIngredientBrixPage />} />
            <Route path="/admin/normalize-target-brix" element={<AdminNormalizeTargetBrixPage />} />
            <Route path="/admin/classify-recipes" element={<AdminClassifyRecipesPage />} />
            <Route path="/admin/brix-quality" element={<AdminBrixQualityPage />} />
            <Route path="/billing" element={<BillingRedirectPage />} />
            <Route path="/admin/recipe-translations" element={<AdminRecipeTranslationsPage />} />
            <Route path="/admin/import-recipes" element={<AdminImportRecipes />} />
            <Route path="/shared/:token" element={<SharedRecipePage />} />
            <Route path="/shares" element={<ManageSharesPage />} />
            {/* Catch-all — must stay last. Triggers when a route is unknown
                (typically a stale cached bundle after a new deploy). Shows
                a clear message with a cache-bypass refresh option. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
      </div>

      {/* Global Ad Slot for guests - shows on all pages except auth pages */}
      {!isAuthPage && <AdSlot placement="bottom_banner" />}

      {/* Mobile Bottom Navigation */}
      {!isAuthPage && (
        <div className="lg:hidden border-t border-gray-200 bg-white fixed bottom-0 left-0 right-0 z-40 shadow-lg">
          <div className="grid grid-cols-5 gap-1 p-2">
            {/* Home */}
            <Link
              to="/"
              className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                location.pathname === '/' ? "bg-cyan-50 text-cyan-600" : "text-gray-600"
              }`}
            >
              <FaHome size={18} />
              <span className="text-xs font-medium">{t('nav.home')}</span>
            </Link>
            
            {/* Recipes */}
            <Link
              to="/recipes"
              className={`relative flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                location.pathname === '/recipes' ? "bg-cyan-50 text-cyan-600" : "text-gray-600"
              }`}
            >
              <FaBook size={18} />
              <span className="text-xs font-medium">{t('nav.recipes')}</span>
              {newRecipesCount > 0 && (
                <span
                  data-testid="mobile-nav-recipes-new-badge"
                  className="absolute top-1 right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-yellow-400 text-gray-900 text-[10px] font-bold rounded-full"
                >
                  {newRecipesCount > 9 ? '9+' : newRecipesCount}
                </span>
              )}
            </Link>
            
            {/* Match */}
            {guest ? (
              <button
                type="button"
                onClick={() => setMobileLockedReason('general')}
                title={t('nav.premiumRequired', 'Kræver medlemskab')}
                data-testid="mobile-nav-match-locked"
                className="flex flex-col items-center gap-1 p-2 rounded-lg transition-colors text-gray-400 relative"
              >
                <FaMagic size={18} />
                <span className="text-xs font-medium">Match</span>
                <FaLock className="absolute top-1 right-2 text-[8px] text-gray-400" />
              </button>
            ) : (
              <Link
                to="/match"
                className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                  location.pathname === '/match' ? "bg-cyan-50 text-cyan-600" : "text-gray-600"
                }`}
              >
                <FaMagic size={18} />
                <span className="text-xs font-medium">Match</span>
              </Link>
            )}

            {/* Liste (Shopping) */}
            {guest ? (
              <button
                type="button"
                onClick={() => setMobileLockedReason('general')}
                title={t('nav.premiumRequired', 'Kræver medlemskab')}
                data-testid="mobile-nav-shopping-locked"
                className="flex flex-col items-center gap-1 p-2 rounded-lg transition-colors text-gray-400 relative"
              >
                <FaShoppingCart size={18} />
                <span className="text-xs font-medium">Liste</span>
                <FaLock className="absolute top-1 right-2 text-[8px] text-gray-400" />
              </button>
            ) : (
              <Link
                to="/shopping"
                className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                  location.pathname === '/shopping' ? "bg-cyan-50 text-cyan-600" : "text-gray-600"
                }`}
              >
                <FaShoppingCart size={18} />
                <span className="text-xs font-medium">Liste</span>
              </Link>
            )}
            
            {/* Settings/Profile */}
            <Link
              to="/profile"
              className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${
                location.pathname === '/profile' ? "bg-cyan-50 text-cyan-600" : "text-gray-600"
              }`}
            >
              <FaCog size={18} />
              <span className="text-xs font-medium">Profil</span>
            </Link>
          </div>
        </div>
      )}

      {/* Floating AI Assistant Button - shown on all non-auth pages */}
      {!isAuthPage && <FloatingAIButton />}

      {/* Upgrade modal triggered when a guest clicks a locked mobile nav item */}
      <UpgradeModal
        isOpen={!!mobileLockedReason}
        onClose={() => setMobileLockedReason(null)}
        reason={mobileLockedReason || 'general'}
        currentUser={user}
        requiredTier="auth"
      />

      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
